<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, FormAlert, Label, NumberField, Select } from '@/shared/ui'

const props = withDefaults(defineProps<{
  footerLayout?: 'inline' | 'stacked'
  mode: 'add' | 'edit'
  serviceId: string | null
  serviceName?: string
  available: { value: string; label: string }[]
  duration: string
  price: string
  buffer: string
  status: 'idle' | 'saving'
  fieldErrors: Partial<Record<'service' | 'duration' | 'price' | 'buffer', string>>
  formError?: string
}>(), {
  footerLayout: 'inline',
})

const emit = defineEmits<{
  'update:serviceId': [value: string | null]
  'update:duration': [value: string]
  'update:price': [value: string]
  'update:buffer': [value: string]
  submit: []
  cancel: []
}>()

const { t } = useI18n()

type I18nKey = Parameters<typeof t>[0]

function parseDuration(val: string): number {
  const n = parseInt(val || '0', 10)
  return isNaN(n) || n < 0 ? 0 : n
}

const internalHours = ref(Math.floor(parseDuration(props.duration) / 60))
const internalMinutes = ref(parseDuration(props.duration) % 60)

watch(() => props.duration, (val) => {
  const total = parseDuration(val)
  internalHours.value = Math.floor(total / 60)
  internalMinutes.value = total % 60
})

function onHoursChange(val: string): void {
  internalHours.value = Math.max(0, parseInt(val || '0', 10) || 0)
  emit('update:duration', String(internalHours.value * 60 + internalMinutes.value))
}

function onMinutesChange(val: string): void {
  internalMinutes.value = Math.max(0, Math.min(59, parseInt(val || '0', 10) || 0))
  emit('update:duration', String(internalHours.value * 60 + internalMinutes.value))
}

function handleSave(): void {
  if (internalHours.value * 60 + internalMinutes.value <= 0) return
  emit('submit')
}
</script>

<template>
  <div
    class="flex flex-col gap-4 px-4 py-4"
    data-testid="service-form"
  >
    <!-- Network/server error -->
    <FormAlert
      v-if="formError"
      data-testid="form-error"
      variant="destructive"
      :message="t(formError as I18nKey)"
    />

    <!-- Service picker: add mode only -->
    <div
      v-if="mode === 'add'"
      class="flex flex-col gap-1.5"
      data-testid="field-service"
    >
      <Label for="ms-service">{{ t('masters.services.form.service') }}</Label>
      <Select
        id="ms-service"
        :model-value="serviceId"
        :options="available"
        :placeholder="t('masters.services.form.servicePlaceholder')"
        :disabled="status === 'saving'"
        :invalid="!!fieldErrors.service"
        :aria-describedby="fieldErrors.service ? 'ms-service-error' : undefined"
        @update:model-value="emit('update:serviceId', $event)"
      />
      <FormAlert
        v-if="fieldErrors.service"
        id="ms-service-error"
        data-testid="service-error"
        :message="t(fieldErrors.service as I18nKey)"
      />
    </div>

    <!-- Edit mode: #context slot (if provided) or service name as subtitle (default) -->
    <template v-else>
      <slot name="context">
        <p
          class="text-sm font-medium text-foreground"
          data-testid="edit-service-name"
        >
          {{ serviceName }}
        </p>
      </slot>
    </template>

    <!-- Duration: hours + minutes -->
    <div
      class="flex flex-col gap-1.5"
      data-testid="field-duration"
    >
      <Label>{{ t('masters.services.form.duration') }}</Label>
      <div class="flex gap-2">
        <div class="flex-1">
          <NumberField
            id="ms-duration-hours"
            :model-value="String(internalHours)"
            :unit="t('masters.services.units.hour')"
            :disabled="status === 'saving'"
            min="0"
            step="1"
            :aria-label="`${t('masters.services.form.duration')} — ${t('masters.services.units.hour')}`"
            :aria-describedby="fieldErrors.duration ? 'ms-duration-error' : undefined"
            @update:model-value="onHoursChange"
          />
        </div>
        <div class="flex-1">
          <NumberField
            id="ms-duration-mins"
            :model-value="String(internalMinutes)"
            :unit="t('masters.services.units.min')"
            :disabled="status === 'saving'"
            min="0"
            max="59"
            step="5"
            :aria-label="`${t('masters.services.form.duration')} — ${t('masters.services.units.min')}`"
            :aria-describedby="fieldErrors.duration ? 'ms-duration-error' : undefined"
            @update:model-value="onMinutesChange"
          />
        </div>
      </div>
      <FormAlert
        v-if="fieldErrors.duration"
        id="ms-duration-error"
        data-testid="duration-error"
        :message="t(fieldErrors.duration as I18nKey)"
      />
    </div>

    <!-- Price -->
    <div
      class="flex flex-col gap-1.5"
      data-testid="field-price"
    >
      <Label for="ms-price">{{ t('masters.services.form.price') }}</Label>
      <NumberField
        id="ms-price"
        :model-value="price"
        unit="€"
        :disabled="status === 'saving'"
        min="0"
        step="0.01"
        :aria-describedby="fieldErrors.price ? 'ms-price-error' : undefined"
        @update:model-value="emit('update:price', $event)"
      />
      <FormAlert
        v-if="fieldErrors.price"
        id="ms-price-error"
        data-testid="price-error"
        :message="t(fieldErrors.price as I18nKey)"
      />
    </div>

    <!-- Buffer -->
    <div
      class="flex flex-col gap-1.5"
      data-testid="field-buffer"
    >
      <Label for="ms-buffer">
        {{ t('masters.services.form.buffer') }}
        <span class="ml-1 text-xs text-muted-foreground">{{ t('masters.services.form.bufferHint') }}</span>
      </Label>
      <NumberField
        id="ms-buffer"
        :model-value="buffer"
        :unit="t('masters.services.units.min')"
        :disabled="status === 'saving'"
        min="0"
        step="1"
        :aria-describedby="fieldErrors.buffer ? 'ms-buffer-error' : undefined"
        @update:model-value="emit('update:buffer', $event)"
      />
      <FormAlert
        v-if="fieldErrors.buffer"
        id="ms-buffer-error"
        data-testid="buffer-error"
        :message="t(fieldErrors.buffer as I18nKey)"
      />
    </div>

    <!-- Composition point: danger-zone or other content injected by features -->
    <slot name="after-fields" />

    <!-- Footer buttons: inline (default) -->
    <div
      v-if="footerLayout === 'inline'"
      class="flex justify-end gap-2 pt-2"
      data-testid="footer-inline"
    >
      <Button
        type="button"
        variant="outline"
        data-testid="btn-cancel"
        @click="emit('cancel')"
      >
        {{ t('masters.services.actions.cancel') }}
      </Button>
      <Button
        type="button"
        data-testid="btn-save"
        :loading="status === 'saving'"
        :disabled="status === 'saving'"
        @click="handleSave"
      >
        {{ t('masters.services.actions.save') }}
      </Button>
    </div>

    <!-- Footer buttons: stacked (primary save on top, ghost cancel below) -->
    <div
      v-else
      class="flex flex-col gap-2 pt-2"
      data-testid="footer-stacked"
    >
      <Button
        type="button"
        class="w-full min-h-11"
        data-testid="btn-save"
        :loading="status === 'saving'"
        :disabled="status === 'saving'"
        @click="handleSave"
      >
        {{ t('masters.services.actions.save') }}
      </Button>
      <Button
        type="button"
        variant="ghost"
        class="w-full min-h-[38px]"
        data-testid="btn-cancel"
        @click="emit('cancel')"
      >
        {{ t('masters.services.actions.cancel') }}
      </Button>
    </div>
  </div>
</template>
